/**
 * Cliente central da API v2 (mesma origem; sessão via cookie do better-auth).
 * Cada endpoint tem uma função tipada. Erros viram ApiError com mensagem amigável.
 */
import type { AuthmeshInput, AuthmeshService, GatewayApi, GatewayApiInput, GatewayConsumer, GatewayRoute, Role, AdminJob, AuditItem, BetaCode, Invitation, InvitationPreview, Member, PricePreview, Webhook, WebhookDelivery, WebhookEvent, AdminOrg, AllowDuration, AllowlistItem, ApiKey, BanDuration, CrowdsecAlert, CrowdsecDecision, CrowdsecOverview, Asset, CreatedApiKey, Domain, DomainEvent, Me, Organization, Page, Plan, PlanCode, Project, RuleByKind, RuleInputByKind, RuleKind, RulePatch, Section, SeoInput, SeoSetting, SpaMode, TailorkitDocument, TailorkitRuleHealth, TailorkitSession, TailorkitState, VerifyResult, WafFailMode, WafMode } from "./api-types";
import { ApiError } from "./errors";
import { getLang } from "@/i18n";
import { getOrgId } from "./org";

export const API_BASE = "/api/v2";

type RequestOptions = {
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Corpo multipart (upload). */
  form?: FormData;
};

/** Ouvintes de 401 (o app redireciona para o login). */
const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  // idioma do painel: mensagens de erro da API seguem a escolha (não só o idioma do navegador)
  const headers: Record<string, string> = { accept: "application/json", "accept-language": getLang(), ...opts.headers };
  const orgId = getOrgId();
  if (orgId) headers["x-org-id"] = orgId;
  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    // URL absoluta na mesma origem (funciona também fora do navegador, ex.: testes).
    const url = new URL(`${API_BASE}${path}`, window.location.origin).toString();
    res = await fetch(url, { method, headers, body, credentials: "include", signal: opts.signal });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError(0, "network");
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const error = ApiError.fromBody(res.status, data);
    if (res.status === 401) unauthorizedListeners.forEach((l) => l());
    throw error;
  }
  return data as T;
}

const enc = encodeURIComponent;

/** Gera uma Idempotency-Key (retries não duplicam domínios). */
export function newIdempotencyKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export const api = {
  // ── conta ────────────────────────────────────────────────
  me: (signal?: AbortSignal) => request<Me>("GET", "/me", { signal }),
  // Equipe e convites.
  members: () => request<{ items: Member[] }>("GET", "/members"),
  setMemberRole: (userId: string, role: Role) => request<{ ok: true }>("PATCH", `/members/${enc(userId)}`, { body: { role } }),
  removeMember: (userId: string) => request<void>("DELETE", `/members/${enc(userId)}`),
  invitations: () => request<{ items: Invitation[] }>("GET", "/invitations"),
  invite: (email: string, role: Role) => request<{ id: string; url: string; emailed: boolean; expiresAt: string }>("POST", "/invitations", { body: { email, role } }),
  revokeInvitation: (id: string) => request<void>("DELETE", `/invitations/${enc(id)}`),
  invitationPreview: (token: string) => request<InvitationPreview>("GET", `/invitations/${enc(token)}`),
  acceptInvitation: (token: string) => request<{ orgId: string }>("POST", `/invitations/${enc(token)}/accept`),
  // Webhooks.
  webhooks: () => request<{ items: Webhook[]; events: WebhookEvent[] }>("GET", "/webhooks"),
  createWebhook: (input: { url: string; events: WebhookEvent[]; description?: string | null }) =>
    request<{ id: string; secret: string }>("POST", "/webhooks", { body: input }),
  updateWebhook: (id: string, patch: { url?: string; events?: WebhookEvent[]; enabled?: boolean; description?: string | null }) =>
    request<{ ok: true }>("PATCH", `/webhooks/${enc(id)}`, { body: patch }),
  deleteWebhook: (id: string) => request<void>("DELETE", `/webhooks/${enc(id)}`),
  testWebhook: (id: string) => request<{ deliveryId: string }>("POST", `/webhooks/${enc(id)}/test`),
  rotateWebhookSecret: (id: string) => request<{ secret: string }>("POST", `/webhooks/${enc(id)}/rotate-secret`),
  webhookDeliveries: (id: string) => request<{ items: WebhookDelivery[] }>("GET", `/webhooks/${enc(id)}/deliveries`),
  redeliver: (deliveryId: string) => request<{ deliveryId: string }>("POST", `/webhook-deliveries/${enc(deliveryId)}/redeliver`),
  // Atividade.
  audit: (opts: { before?: number; action?: string } = {}) =>
    request<{ items: AuditItem[]; next: number | null }>(
      "GET",
      `/audit?limit=50${opts.before ? `&before=${opts.before}` : ""}${opts.action ? `&action=${enc(opts.action)}` : ""}`,
    ),
  rotateApiKey: (id: string, graceHours: number) =>
    request<CreatedApiKey & { oldValidUntil: string }>("POST", `/api-keys/${enc(id)}/rotate`, { body: { graceHours } }),
  pricePreview: (input: { plan?: PlanCode | null; addDomains?: number }) => request<PricePreview>("POST", "/billing/preview", { body: input }),
  billingSettings: (input: { autoExtraPacks?: boolean }) => request<{ autoExtraPacks: boolean }>("PUT", "/billing/settings", { body: input }),
  // Administração: suspensão, jobs, códigos beta.
  adminSuspend: (orgId: string, reason: string) => request<{ suspended: boolean }>("POST", `/admin/orgs/${enc(orgId)}/suspend`, { body: { reason } }),
  adminUnsuspend: (orgId: string) => request<{ suspended: boolean }>("POST", `/admin/orgs/${enc(orgId)}/unsuspend`),
  adminJobs: (state: "failed" | "retry" | "active" = "failed") => request<{ items: AdminJob[] }>("GET", `/admin/jobs?state=${state}`),
  adminRetryJob: (id: string) => request<{ ok: true; name: string }>("POST", `/admin/jobs/${enc(id)}/retry`),
  adminBetaCodes: () => request<{ items: BetaCode[] }>("GET", "/admin/beta-codes"),
  adminCreateBetaCodes: (input: { count: number; note?: string | null; prefix?: string | null }) =>
    request<{ codes: string[] }>("POST", "/admin/beta-codes", { body: input }),
  adminSetBetaCode: (code: string, active: boolean) => request<{ code: string; active: boolean }>("PATCH", `/admin/beta-codes/${enc(code)}`, { body: { active } }),
  // Administração da plataforma.
  adminOrgs: (q = "") => request<{ items: AdminOrg[] }>("GET", `/admin/orgs${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  adminSetPlan: (orgId: string, plan: PlanCode | null) => request<{ orgId: string; plan: PlanCode | null }>("PUT", `/admin/orgs/${enc(orgId)}/plan`, { body: { plan } }),
  adminVerifyProject: (projectId: string) => request<{ projectId: string; verified: boolean }>("POST", `/admin/projects/${enc(projectId)}/verify`),
  crowdsecOverview: (signal?: AbortSignal) => request<CrowdsecOverview>("GET", "/admin/crowdsec/overview", { signal }),
  crowdsecDecisions: (q = "", signal?: AbortSignal) =>
    request<{ items: CrowdsecDecision[]; total: number }>("GET", `/admin/crowdsec/decisions${q ? `?q=${encodeURIComponent(q)}` : ""}`, { signal }),
  crowdsecBan: (body: { value: string; duration: BanDuration; reason: string }) =>
    request<{ value: string; scope: string; duration: BanDuration }>("POST", "/admin/crowdsec/decisions", { body }),
  crowdsecUnban: (id: number) => request<{ deleted: number }>("DELETE", `/admin/crowdsec/decisions/${id}`),
  crowdsecUnbanIp: (ip: string) => request<{ deleted: number }>("DELETE", `/admin/crowdsec/decisions?ip=${encodeURIComponent(ip)}`),
  crowdsecAlerts: (f: { hours: number; ip?: string; host?: string; scenario?: string; kind?: string }, signal?: AbortSignal) => {
    const p = new URLSearchParams({ hours: String(f.hours) });
    for (const k of ["ip", "host", "scenario", "kind"] as const) if (f[k]) p.set(k, f[k]!);
    return request<{ items: CrowdsecAlert[] }>("GET", `/admin/crowdsec/alerts?${p.toString()}`, { signal });
  },
  crowdsecAllowlist: (signal?: AbortSignal) => request<{ items: AllowlistItem[] }>("GET", "/admin/crowdsec/allowlist", { signal }),
  crowdsecAllow: (body: { value: string; comment: string; duration: AllowDuration }) =>
    request<{ id: string; value: string; expiresAt: string | null; unbanned: number }>("POST", "/admin/crowdsec/allowlist", { body }),
  crowdsecUnallow: (id: string) => request<{ id: string; value: string }>("DELETE", `/admin/crowdsec/allowlist/${enc(id)}`),
  /** Perfil e origem do lead (wizard de cadastro) e nome da organização. */
  updateOrganization: (patch: { name?: string; profile?: string; referralSource?: string; notifyDomainStatus?: boolean; locale?: "pt-BR" | "en" }) =>
    request<Organization>("PATCH", "/organization", { body: patch }),

  // ── plano Scale: AuthMesh e API Gateway ─────────────────
  authmesh: (projectId: string) => request<{ items: AuthmeshService[] }>("GET", `/projects/${enc(projectId)}/authmesh`),
  createAuthmesh: (projectId: string, input: AuthmeshInput) => request<AuthmeshService>("POST", `/projects/${enc(projectId)}/authmesh`, { body: input }),
  updateAuthmesh: (projectId: string, id: string, input: AuthmeshInput) =>
    request<AuthmeshService>("PATCH", `/projects/${enc(projectId)}/authmesh/${enc(id)}`, { body: input }),
  deleteAuthmesh: (projectId: string, id: string) => request<void>("DELETE", `/projects/${enc(projectId)}/authmesh/${enc(id)}`),
  gatewayApis: (projectId: string) => request<{ items: GatewayApi[] }>("GET", `/projects/${enc(projectId)}/gateway`),
  createGatewayApi: (projectId: string, input: GatewayApiInput) =>
    request<GatewayApi & { signingSecret: string }>("POST", `/projects/${enc(projectId)}/gateway`, { body: input }),
  updateGatewayApi: (projectId: string, id: string, input: GatewayApiInput) =>
    request<GatewayApi>("PATCH", `/projects/${enc(projectId)}/gateway/${enc(id)}`, { body: input }),
  deleteGatewayApi: (projectId: string, id: string) => request<void>("DELETE", `/projects/${enc(projectId)}/gateway/${enc(id)}`),
  rotateGatewaySecret: (projectId: string, id: string) =>
    request<{ signingSecret: string }>("POST", `/projects/${enc(projectId)}/gateway/${enc(id)}/rotate-secret`),
  gatewayRoutes: (projectId: string, apiId: string) => request<{ items: GatewayRoute[] }>("GET", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/routes`),
  addGatewayRoute: (
    projectId: string,
    apiId: string,
    input: { method?: string; pathPattern: string; upstreamUrl: string; authRequired?: boolean; scopes?: string[]; stripPrefix?: boolean },
  ) => request<GatewayRoute>("POST", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/routes`, { body: input }),
  deleteGatewayRoute: (projectId: string, apiId: string, routeId: string) =>
    request<void>("DELETE", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/routes/${enc(routeId)}`),
  gatewayConsumers: (projectId: string, apiId: string) =>
    request<{ items: GatewayConsumer[] }>("GET", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/consumers`),
  createGatewayConsumer: (projectId: string, apiId: string, input: { name: string; externalId?: string; scopes?: string[]; rateLimitPerMin?: number | null }) =>
    request<GatewayConsumer>("POST", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/consumers`, { body: input }),
  issueConsumerKey: (projectId: string, apiId: string, consumerId: string, expiresAt: string | null) =>
    request<{ id: string; prefix: string; secret: string; expiresAt: string | null }>(
      "POST",
      `/projects/${enc(projectId)}/gateway/${enc(apiId)}/consumers/${enc(consumerId)}/keys`,
      { body: { expiresAt } },
    ),
  revokeConsumerKey: (projectId: string, apiId: string, keyId: string) =>
    request<void>("DELETE", `/projects/${enc(projectId)}/gateway/${enc(apiId)}/keys/${enc(keyId)}`),

  // ── projetos ─────────────────────────────────────────────
  listProjects: () => request<Page<Project>>("GET", "/projects"),
  getProject: (id: string) => request<Project>("GET", `/projects/${enc(id)}`),
  createProject: (input: { name: string; originUrl: string }) => request<Project>("POST", "/projects", { body: input }),
  updateProject: (
    id: string,
    patch: { name?: string; originUrl?: string; extraOrigins?: string[]; spaMode?: SpaMode; spaInitialRouteVar?: boolean; blockedPaths?: string[] },
  ) => request<Project>("PATCH", `/projects/${enc(id)}`, { body: patch }),
  deleteProject: (id: string) => request<void>("DELETE", `/projects/${enc(id)}`),
  verifyProject: (id: string) => request<VerifyResult>("POST", `/projects/${enc(id)}/verify`),

  listProjectRules: <K extends RuleKind>(projectId: string, kind: K) =>
    request<Page<RuleByKind[K]>>("GET", `/projects/${enc(projectId)}/rules/${kind}`),
  createProjectRule: <K extends RuleKind>(projectId: string, kind: K, input: RuleInputByKind[K]) =>
    request<RuleByKind[K]>("POST", `/projects/${enc(projectId)}/rules/${kind}`, { body: input }),

  // ── domínios ─────────────────────────────────────────────
  listDomains: (q: { projectId?: string; cursor?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (q.projectId) params.set("projectId", q.projectId);
    if (q.cursor) params.set("cursor", q.cursor);
    if (q.limit) params.set("limit", String(q.limit));
    const qs = params.toString();
    return request<Page<Domain>>("GET", `/domains${qs ? `?${qs}` : ""}`);
  },
  getDomain: (id: string) => request<Domain>("GET", `/domains/${enc(id)}`),
  createDomain: (
    input: {
      projectId: string;
      hostname: string;
      name?: string;
      faviconUrl?: string | null;
      overrides?: Section[];
      acceptExtraCharge?: boolean;
    },
    idempotencyKey: string,
  ) => request<Domain>("POST", "/domains", { body: input, headers: { "idempotency-key": idempotencyKey } }),
  updateDomain: (
    id: string,
    patch: {
      name?: string;
      overrides?: Section[];
      wafMode?: WafMode;
      wafFailMode?: WafFailMode;
      assetsDirect?: boolean;
      htmlCacheSeconds?: 0 | 60 | 300 | 3600;
      blockedPaths?: string[];
      faviconUrl?: string | null;
      enabled?: boolean;
    },
  ) => request<Domain>("PATCH", `/domains/${enc(id)}`, { body: patch }),
  deleteDomain: (id: string) => request<void>("DELETE", `/domains/${enc(id)}`),
  recheckDomain: (id: string) => request<Domain>("POST", `/domains/${enc(id)}/recheck`),
  domainEvents: (id: string) => request<Page<DomainEvent>>("GET", `/domains/${enc(id)}/events`),

  listDomainRules: <K extends RuleKind>(domainId: string, kind: K, effective: boolean) =>
    request<Page<RuleByKind[K]>>("GET", `/domains/${enc(domainId)}/rules/${kind}?effective=${effective}`),
  createDomainRule: <K extends RuleKind>(domainId: string, kind: K, input: RuleInputByKind[K]) =>
    request<RuleByKind[K]>("POST", `/domains/${enc(domainId)}/rules/${kind}`, { body: input }),

  // ── regras (por id) ──────────────────────────────────────
  updateRule: <K extends RuleKind>(kind: K, ruleId: string, patch: RulePatch<K>) =>
    request<RuleByKind[K]>("PATCH", `/rules/${kind}/${enc(ruleId)}`, { body: patch }),
  deleteRule: (kind: RuleKind, ruleId: string) => request<void>("DELETE", `/rules/${kind}/${enc(ruleId)}`),

  // ── SEO ──────────────────────────────────────────────────
  listSeo: (domainId: string) => request<Page<SeoSetting>>("GET", `/domains/${enc(domainId)}/seo`),
  upsertSeo: (domainId: string, input: SeoInput) => request<SeoSetting>("PUT", `/domains/${enc(domainId)}/seo`, { body: input }),
  deleteSeo: (domainId: string, seoId: string) => request<void>("DELETE", `/domains/${enc(domainId)}/seo/${enc(seoId)}`),

  // ── arquivos ─────────────────────────────────────────────
  uploadAsset: (file: File) => {
    const form = new FormData();
    form.set("file", file);
    return request<Asset>("POST", "/assets", { form });
  },

  // ── chaves de API ────────────────────────────────────────
  listApiKeys: () => request<Page<ApiKey>>("GET", "/api-keys"),
  createApiKey: (input: { name: string; projectId?: string | null; scopes?: string[] }) =>
    request<CreatedApiKey>("POST", "/api-keys", { body: input }),
  revokeApiKey: (id: string) => request<void>("DELETE", `/api-keys/${enc(id)}`),

  // ── billing ──────────────────────────────────────────────
  listPlans: () => request<Page<Plan>>("GET", "/billing/plans"),
  checkout: (input: { plan: PlanCode; betaCode?: string }) => request<{ url: string | null }>("POST", "/billing/checkout", { body: input }),
  portal: () => request<{ url: string }>("POST", "/billing/portal"),
  changePlan: (plan: PlanCode) => request<{ ok: boolean }>("POST", "/billing/change-plan", { body: { plan } }),

  // ── TailorKit ────────────────────────────────────────────
  getTailorkit: (domainId: string) => request<TailorkitState>("GET", `/domains/${enc(domainId)}/tailorkit`),
  tailorkitSession: (domainId: string) => request<TailorkitSession>("GET", `/domains/${enc(domainId)}/tailorkit/session`),
  saveTailorkitDraft: (domainId: string, document: TailorkitDocument) =>
    request<{ version: number }>("PUT", `/domains/${enc(domainId)}/tailorkit/draft`, { body: { document } }),
  publishTailorkit: (domainId: string) => request<{ version: number }>("POST", `/domains/${enc(domainId)}/tailorkit/publish`),
  rollbackTailorkit: (domainId: string, version: number) =>
    request<{ version: number }>("POST", `/domains/${enc(domainId)}/tailorkit/rollback/${version}`),
  scheduleTailorkit: (domainId: string, at: string) =>
    request<{ version: number; at: string }>("POST", `/domains/${enc(domainId)}/tailorkit/schedule`, { body: { at } }),
  cancelTailorkitSchedule: (domainId: string) =>
    request<{ canceled: boolean }>("DELETE", `/domains/${enc(domainId)}/tailorkit/schedule`),
  /**
   * Regras que não encontraram o elemento (beacon tk_miss, últimos 7 dias). Opcional para o editor:
   * [] quando o analytics não está disponível (404/403/501/503).
   */
  tailorkitHealth: async (domainId: string): Promise<TailorkitRuleHealth[]> => {
    try {
      const res = await request<TailorkitRuleHealth[] | { items?: TailorkitRuleHealth[] } | null>(
        "GET",
        `/domains/${enc(domainId)}/analytics/tailorkit`,
      );
      return Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
    } catch (err) {
      if (err instanceof ApiError && [403, 404, 501, 503].includes(err.status)) return [];
      throw err;
    }
  },
};

export type Api = typeof api;
